import { Trash2, ChevronDown, ChevronUp } from 'lucide-react';
import { useState } from 'react';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { cn } from '@/lib/utils';
import { TRIGGER_ACTIONS, createDefaultAction } from '../config/actions';
import type { TriggerAction, TriggerActionType } from '../types';
import { NotificationForm } from './ActionForms/NotificationForm';
import { EmailForm } from './ActionForms/EmailForm';
import { AccessForm } from './ActionForms/AccessForm';
import { BroadcastForm } from './ActionForms/BroadcastForm';
import { StatusForm } from './ActionForms/StatusForm';
import { WebhookForm } from './ActionForms/WebhookForm';
import { ChannelPassForm } from './ActionForms/ChannelPassForm';
import { FamilySlotsForm } from './ActionForms/FamilySlotsForm';
import { ChannelInviteForm } from './ActionForms/ChannelInviteForm';

interface ActionCardProps {
  action: TriggerAction;
  index: number;
  onChange: (action: TriggerAction) => void;
  onRemove: () => void;
  canRemove: boolean;
}

/**
 * ActionCard
 * 
 * Карточка действия триггера
 * 
 * @returns {React.ReactElement} React компонент
 */
export function ActionCard({ action, index, onChange, onRemove, canRemove }: ActionCardProps) {
  const [isExpanded, setIsExpanded] = useState(true);
  const config = TRIGGER_ACTIONS[action.type];

  const handleTypeChange = (type: TriggerActionType) => {
    onChange(createDefaultAction(type));
  };

  const handleUpdate = (patch: Partial<TriggerAction>) => {
    onChange({ ...action, ...patch });
  };

  const renderForm = () => {
    switch (action.type) {
      case 'send_notification':
        return <NotificationForm action={action} onChange={handleUpdate} />;
      case 'send_email':
        return <EmailForm action={action} onChange={handleUpdate} />;
      case 'grant_access':
      case 'revoke_access':
        return <AccessForm action={action} onChange={handleUpdate} />;
      case 'send_broadcast':
        return <BroadcastForm action={action} onChange={handleUpdate} />;
      case 'update_status':
        return <StatusForm action={action} onChange={handleUpdate} />;
      case 'webhook':
        return <WebhookForm action={action} onChange={handleUpdate} />;
      case 'deactivate_channel_pass':
      case 'hide_channel_pass':
        return <ChannelPassForm action={action} onChange={handleUpdate} />;
      case 'change_family_slots':
        return <FamilySlotsForm action={action} onChange={handleUpdate} />;
      case 'send_channel_invite':
        return <ChannelInviteForm action={action} onChange={handleUpdate} />;
      default:
        return null;
    }
  };

  return (
    <div className="border rounded-lg overflow-hidden">
      <div
        className={cn(
          'flex items-center gap-3 p-3 bg-muted/30',
          isExpanded && 'border-b'
        )}
      >
        <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary text-xs font-medium">
          {index + 1}
        </span>

        <Select value={action.type} onValueChange={handleTypeChange}>
          <SelectTrigger className="flex-1 h-8">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {(Object.entries(TRIGGER_ACTIONS) as Array<[TriggerActionType, typeof config]>).map(
              ([key, cfg]) => (
                <SelectItem key={key} value={key}>
                  <span className="flex items-center gap-2">
                    <cfg.icon className="h-4 w-4" />
                    {cfg.label}
                  </span>
                </SelectItem>
              )
            )}
          </SelectContent>
        </Select>

        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="h-8 w-8"
          onClick={() => setIsExpanded(!isExpanded)}
        >
          {isExpanded ? (
            <ChevronUp className="h-4 w-4" />
          ) : (
            <ChevronDown className="h-4 w-4" />
          )}
        </Button>

        {canRemove && (
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="h-8 w-8 text-destructive hover:text-destructive"
            onClick={onRemove}
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        )}
      </div>

      {isExpanded && (
        <div className="p-4">
          <p className="text-sm text-muted-foreground mb-4">{config.description}</p>
          {renderForm()}
        </div>
      )}
    </div>
  );
}
